import { TemplateElement, html, defineElement } from '@webtides/element-js';
import NumberFormatter from '../../../js/utils/number-formatter';
import './slot-track';

import style from './animated-digit.css';

class AnimatedDigit extends TemplateElement {
    constructor() {
        super({ styles: [style] });
    }

    properties() {
        return {
            duration: 2000,
            end: 0,
            animatedDigit: 0,
            animationType: '',
            animated: true,
            useNumberFormatting: true,
            hasIntersection: false,
        };
    }

    connected() {
        if (!this.animated) return;
        this._intersectionObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        if (this.animationType === 'default') {
                            this.defaultAnimation();
                        }
                        this.hasIntersection = true;
                        this._intersectionObserver.unobserve(this);
                    }
                });
            },
            {
                rootMargin: '0px 0px 0px 0px',
                threshold: 1,
            },
        );

        this._intersectionObserver.observe(this);
    }

    convertDigits(progress = false) {
        if (this.end.toString().indexOf(',') != -1) {
            return this.convertDigitWithComma(progress);
        } else {
            return this.convertSimpleDigit(progress);
        }
    }

    convertDigitWithComma(progress) {
        const decimalDigits = this.end.split(',');
        let digits;
        let decimals;
        if (progress) {
            digits = Math.floor(progress * (parseInt(decimalDigits[0]) - 0));
            decimals = Math.floor(progress * (parseInt(decimalDigits[1]) - 0));
        } else {
            digits = decimalDigits[0];
            decimals = decimalDigits[1];
        }

        return this.useNumberFormatting
            ? NumberFormatter.formattedNumber(digits).concat(',').concat(decimals)
            : digits.concat(',').concat(decimals);
    }
    convertSimpleDigit(progress) {
        const fullDigits = progress ? Math.floor(progress * (parseInt(this.end) - 0)) : this.end;
        return this.useNumberFormatting ? NumberFormatter.formattedNumber(fullDigits) : fullDigits.toString();
    }

    defaultAnimation() {
        let startTimestamp = null;
        const step = (timestamp) => {
            if (!startTimestamp) startTimestamp = timestamp;
            const progress = Math.min((timestamp - startTimestamp) / this.duration, 1);
            this.animatedDigit = this.convertDigits(progress);

            if (progress < 1) {
                window.requestAnimationFrame(step);
            }
        };
        window.requestAnimationFrame(step);

        this._intersectionObserver.unobserve(this);
    }

    slotMachineAnimation() {
        const digitString = this.convertDigits();
        const digits = digitString.split('');

        return digits.map((digit) => {
            if (digit.indexOf(',') != -1) {
                return html`
                    <span class="point">,</span>
                `;
            }
            if (digit.indexOf('.') != -1) {
                return html`
                    <span class="point">.</span>
                `;
            }

            if (digit !== '.' || digit !== ',') {
                return html`
                    <div class="track-fade-out relative">
                        <span class="invisible">0</span>
                        <slot-track start-animation="${this.hasIntersection}" animate-to-value="${digit}"></slot-track>
                    </div>
                `;
            }
        });
    }

    template() {
        switch (this.animationType) {
            case 'default':
                return html`
                    <div>${this.animatedDigit}</div>
                `;
            case 'slotMachine':
                return html`
                    <div class="counter animated text-center">${this.slotMachineAnimation()}</div>
                `;
            default:
                return html`
                    ${this.convertDigits()}
                `;
        }
    }
}

export function define() {
    defineElement('animated-digit', AnimatedDigit);
}
